import { enhancedVTOResultApi } from '../redux/apis/endpoints/vtoResultApi';

/**
 * VirtualTryOnService for SUSU Closet platform
 * Handles virtual try-on functionality using Google Vertex AI
 */
class VirtualTryOnService {
  constructor() {
    this.backendUrl = this.getBackendUrl();
    this.isEnabled = true;
    this.cache = new Map(); // Simple in-memory cache for results
    this.maxCacheSize = 50; // Maximum number of cached results
    this.store = null; // Redux store reference for RTK Query mutations
  }

  /**
   * Set Redux store reference for RTK Query integration
   * @param {Object} store - Redux store instance
   */
  setStore(store) {
    this.store = store;
  }

  /**
   * Detect environment and return appropriate backend URL
   * @returns {string} Backend API URL
   */
  getBackendUrl() {
    const hostname = window.location.hostname;
    const isDev = hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.startsWith('192.168.') ||
      hostname.startsWith('10.') ||
      hostname.startsWith('172.');

    let url = isDev
      ? process.env.REACT_APP_backend_baseUrl_localTest
      : process.env.REACT_APP_backend_baseUrl_live;
      
    if (!url) url = 'http://localhost:1337';
    
    // Auto-detect Strapi v5 vs v3 based on port
    if (url.includes(':1337') && !url.endsWith('/api')) {
      return `${url}/api`;
    }
    
    return url;
  }

  /**
   * Get authentication token from session storage
   * @returns {string|null} JWT token
   */
  getAuthToken() {
    // Try multiple possible token storage locations (based on existing codebase patterns)
    const token = localStorage.getItem('token') ||       // Most common in existing code
      localStorage.getItem('auth_token') ||  // AuthService token
      localStorage.getItem('jwt') ||         // JWT token storage
      sessionStorage.getItem('jwt') ||       // Session JWT
      sessionStorage.getItem('token');       // Session token

    console.log('[VTOService] Token retrieval debug:', {
      hasToken: !!token,
      tokenSource: token ? (
        localStorage.getItem('token') ? 'localStorage.token' :
          localStorage.getItem('auth_token') ? 'localStorage.auth_token' :
            localStorage.getItem('jwt') ? 'localStorage.jwt' :
              sessionStorage.getItem('jwt') ? 'sessionStorage.jwt' :
                'sessionStorage.token'
      ) : 'none',
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'none'
    });

    return token;
  }

  /**
   * Try on a single garment with a person image
   * @param {string} personImage - Base64 encoded person image
   * @param {string} garmentImage - Base64 encoded garment image
   * @param {Object} options - Try-on options
   * @returns {Promise<Object>} Try-on result
   */
  async tryOnGarment(personImage, garmentImage, options = {}) {
    try {
      // Validate inputs
      if (!personImage || !garmentImage) {
        throw new Error('Both person image and garment image are required');
      }

      // Generate cache key
      const cacheKey = this.generateCacheKey(personImage, garmentImage, options);

      // DISABLED: Frontend cache doesn't account for layering (currentlyWornItemIds)
      // Backend has proper caching with full layering support
      // Check cache first
      // if (this.cache.has(cacheKey)) {
      //   console.log('Returning cached virtual try-on result');
      //   return {
      //     ...this.cache.get(cacheKey),
      //     cached: true
      //   };
      // }

      // Validate and compress images before API call
      await this.validateImages(personImage, garmentImage);

      // Compress images if they're too large
      const compressedImages = await this.compressImagesIfNeeded(personImage, garmentImage);
      const finalPersonImage = compressedImages.personImage;
      const finalGarmentImage = compressedImages.garmentImage;

      // Prepare request data - support both old (personImage URL) and new (baseImageSource) formats
      const requestData = {
        personImage: finalPersonImage, // LEGACY: Current canvas person image (VTO result or original)
        garmentImage: finalGarmentImage,
        garmentType: options.garmentType,
        options: {
          preserveBackground: options.preserveBackground !== false,
          fitPreference: options.fitPreference || 'fitted',
          // Send IDs for caching
          originalPersonImageId: options.originalPersonImageId, // For caching
          currentlyWornItemIds: options.currentlyWornItemIds || [], // For caching
          garmentId: options.garmentId,
          userId: options.userId,
          // Multi-item support
          layeredItems: options.layeredItems || [],
          ...options
        }
      };

      console.log('[VTOService] Using legacy tryOnGarment method with personImage URL. Consider using tryOnGarmentWithBackendSave with baseImageSource.');

      // Make async API call (returns immediately with jobId)
      const response = await this.makeApiCall('/virtual-try-on/try-on-async', 'POST', requestData);

      if (!response.success || !response.jobId) {
        // A shared try-on without a subscription isn't a failure to retry — the
        // caller should open the subscription modal, so carry the code through.
        if (response.code === 'purchase_required' || response.code === 'subscription_required') {
          const err = new Error(response.message || 'Buy credits or subscribe to continue');
          err.code = response.code;
          throw err;
        }
        throw new Error(response.message || 'Failed to start virtual try-on');
      }

      // Poll for result
      const result = await this.pollForResult(response.jobId);

      if (result.status === 'completed') {
        return {
          ...result.result,
          cached: false
        };
      } else if (result.status === 'failed') {
        throw new Error(result.error || 'Virtual try-on failed');
      } else {
        throw new Error('Virtual try-on timed out');
      }

    } catch (error) {
      console.error('Virtual try-on error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Generate try-on with person image and clothing items (for Redux integration)
   * @param {Object} params - Generation parameters
   * @param {string} params.personImage - Base64 encoded person image
   * @param {Array} params.clothingItems - Array of clothing item objects
   * @param {Object} params.options - Try-on options
   * @returns {Promise<Object>} Try-on result
   */
  async generateTryOn({ personImage, clothingItems, options = {} }) {
    try {
      if (!personImage || !clothingItems || clothingItems.length === 0) {
        throw new Error('Person image and at least one clothing item are required');
      }

      // Convert clothing items to garments format
      const garments = clothingItems.map(item => ({
        id: item.id,
        image: item.image || item.imageUrl,
        type: item.type || item.category,
        name: item.name
      }));

      // For single garment, use direct try-on
      if (garments.length === 1) {
        const result = await this.tryOnGarment(personImage, garments[0].image, {
          ...options,
          garmentType: garments[0].type
        });

        return {
          success: true,
          imageUrl: result.imageUrl,
          garments: garments
        };
      }

      // For multiple garments, use batch processing
      const result = await this.tryOnMultipleGarments(personImage, garments, options);

      if (result.success && result.results.length > 0) {
        // Return the last successful result for now
        const lastSuccess = result.results.filter(r => r.success).pop();

        return {
          success: true,
          imageUrl: lastSuccess?.imageUrl,
          garments: garments
        };
      }

      throw new Error('No successful try-on results');
    } catch (error) {
      console.error('Generate try-on error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Try on multiple garments with a person image
   * @param {string} personImage - Base64 encoded person image
   * @param {Array} garments - Array of garment objects with image and type
   * @param {Object} options - Try-on options
   * @returns {Promise<Object>} Try-on results
   */
  async tryOnMultipleGarments(personImage, garments, options = {}) {
    try {
      if (!personImage || !garments || garments.length === 0) {
        throw new Error('Person image and at least one garment are required');
      }

      // For now, process garments individually
      // Future enhancement: batch processing
      const results = [];

      for (const garment of garments) {
        try {
          const result = await this.tryOnGarment(personImage, garment.image, {
            ...options,
            garmentType: garment.type
          });

          results.push({
            garmentId: garment.id,
            garmentType: garment.type,
            success: true,
            ...result
          });
        } catch (error) {
          results.push({
            garmentId: garment.id,
            garmentType: garment.type,
            success: false,
            error: error.message
          });
        }
      }

      return {
        success: true,
        results,
        totalGarments: garments.length,
        successfulTryOns: results.filter(r => r.success).length
      };

    } catch (error) {
      console.error('Multiple garment try-on error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Validate image quality before try-on
   * @param {string} image - Base64 encoded image
   * @param {string} imageType - Type of image (person/garment)
   * @returns {Promise<Object>} Validation result
   */
  async validateImage(image, imageType = 'unknown') {
    try {
      const response = await this.makeApiCall('/virtual-try-on/validate-image', 'POST', {
        image,
        imageType
      });

      return response;
    } catch (error) {
      console.error('Image validation error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Optimize image for virtual try-on
   * @param {string} image - Base64 encoded image
   * @param {Object} options - Optimization options
   * @returns {Promise<Object>} Optimization result
   */
  async optimizeImage(image, options = {}) {
    try {
      const response = await this.makeApiCall('/virtual-try-on/optimize-image', 'POST', {
        image,
        options
      });

      return response;
    } catch (error) {
      console.error('Image optimization error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get usage statistics for the current user
   * @returns {Promise<Object>} Usage statistics
   */
  async getUsageStats() {
    try {
      const response = await this.makeApiCall('/virtual-try-on/usage-stats', 'GET');
      return response;
    } catch (error) {
      console.error('Usage stats error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Check if virtual try-on is available for the user
   * @param {string} personImage - Base64 encoded person image
   * @returns {Promise<Object>} Availability check result
   */
  async checkAvailability(personImage) {
    try {
      if (!personImage) {
        return {
          available: false,
          reason: 'no_person_image',
          message: 'Please upload a person image to use virtual try-on'
        };
      }

      // Basic client-side validation only
      if (!this.isValidBase64Image(personImage)) {
        return {
          available: false,
          reason: 'invalid_person_image',
          message: 'Invalid person image format'
        };
      }

      // Check if image is too large
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (this.getBase64Size(personImage) > maxSize) {
        return {
          available: false,
          reason: 'image_too_large',
          message: 'Person image is too large (maximum 10MB)'
        };
      }

      // Check if we have authentication token
      const token = this.getAuthToken();
      if (!token) {
        return {
          available: false,
          reason: 'not_authenticated',
          message: 'Please log in to use virtual try-on'
        };
      }

      // For now, assume it's available if basic checks pass
      // We'll do more detailed validation when actually making the try-on request
      return {
        available: true,
        message: 'Virtual try-on is available'
      };

    } catch (error) {
      console.error('Availability check error:', error);
      return {
        available: false,
        reason: 'service_error',
        message: 'Virtual try-on service temporarily unavailable'
      };
    }
  }

  // Helper methods

  /**
   * Validate images before processing
   * @param {string} personImage - Base64 encoded person image
   * @param {string} garmentImage - Base64 encoded garment image
   */
  async validateImages(personImage, garmentImage) {
    // Basic client-side validation
    if (!this.isValidBase64Image(personImage)) {
      throw new Error('Invalid person image format');
    }

    if (!this.isValidBase64Image(garmentImage)) {
      throw new Error('Invalid garment image format');
    }

    // Check file size limits (10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes

    if (this.getBase64Size(personImage) > maxSize) {
      throw new Error('Person image is too large (maximum 10MB)');
    }

    if (this.getBase64Size(garmentImage) > maxSize) {
      throw new Error('Garment image is too large (maximum 10MB)');
    }
  }

  /**
   * Check if string is valid base64 image or URL
   * @param {string} imageString - Base64 string or URL to validate
   * @returns {boolean} True if valid
   */
  isValidBase64Image(imageString) {
    if (!imageString || typeof imageString !== 'string') {
      return false;
    }

    // Check for HTTP/HTTPS URL format (from backend)
    const urlPattern = /^https?:\/\/.+\.(jpeg|jpg|png|webp|gif)/i;
    if (urlPattern.test(imageString)) {
      return true;
    }

    // Check for Cloudinary URL (common backend image host)
    if (imageString.includes('cloudinary.com') || imageString.includes('res.cloudinary')) {
      return true;
    }

    // Check for data URL format (base64)
    const dataUrlPattern = /^data:image\/(jpeg|jpg|png|webp);base64,/;
    if (!dataUrlPattern.test(imageString)) {
      return false;
    }

    // Extract base64 data
    const base64Data = imageString.replace(/^data:image\/[a-z]+;base64,/, '');

    // Basic base64 validation
    try {
      return btoa(atob(base64Data)) === base64Data;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get size of base64 encoded image in bytes
   * @param {string} imageString - Base64 string or URL
   * @returns {number} Size in bytes (returns 0 for URLs since we can't determine size client-side)
   */
  getBase64Size(imageString) {
    // If it's a URL, we can't determine size client-side, return 0 to skip size checks
    if (imageString.startsWith('http://') || imageString.startsWith('https://')) {
      return 0;
    }

    const base64Data = imageString.replace(/^data:image\/[a-z]+;base64,/, '');
    return Math.ceil(base64Data.length * 0.75); // Base64 is ~33% larger than binary
  }

  /**
   * Compress images if they exceed size limits
   * @param {string} personImage - Person image base64 or URL
   * @param {string} garmentImage - Garment image base64 or URL
   * @returns {Promise<Object>} Compressed images
   */
  async compressImagesIfNeeded(personImage, garmentImage) {
    const maxSize = 9 * 1024 * 1024; // 9MB limit (under the 10MB backend cap)

    let finalPersonImage = personImage;
    let finalGarmentImage = garmentImage;

    // Skip compression for URLs (backend images are already optimized)
    const isPersonImageUrl = personImage.startsWith('http://') || personImage.startsWith('https://');
    const isGarmentImageUrl = garmentImage.startsWith('http://') || garmentImage.startsWith('https://');

    // Compress person image if needed (only for base64)
    if (!isPersonImageUrl && this.getBase64Size(personImage) > maxSize) {
      console.log('[VTOService] Compressing person image to stay under 10MB limits');
      finalPersonImage = await this.compressImage(personImage, 0.9);
    }

    // Compress garment image if needed (only for base64)
    if (!isGarmentImageUrl && this.getBase64Size(garmentImage) > maxSize) {
      console.log('[VTOService] Compressing garment image to stay under 10MB limits');
      finalGarmentImage = await this.compressImage(garmentImage, 0.9);
    }

    return {
      personImage: finalPersonImage,
      garmentImage: finalGarmentImage
    };
  }

  /**
   * Compress a single image
   * @param {string} base64Image - Base64 image to compress
   * @param {number} quality - Compression quality (0-1)
   * @returns {Promise<string>} Compressed base64 image
   */
  async compressImage(base64Image, quality = 0.9) {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Calculate new dimensions (max 2048x2048 to preserve detail)
        let { width, height } = img;
        const maxDimension = 2048;

        if (width > maxDimension || height > maxDimension) {
          const ratio = Math.min(maxDimension / width, maxDimension / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        try {
          const compressedImage = canvas.toDataURL('image/jpeg', quality);
          const originalSize = this.getBase64Size(base64Image);
          const compressedSize = this.getBase64Size(compressedImage);
          const reduction = Math.round((1 - compressedSize / originalSize) * 100);

          console.log(`[VTOService] Image compressed: ${reduction}% size reduction`);
          resolve(compressedImage);
        } catch (error) {
          reject(new Error('Failed to compress image'));
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image for compression'));
      };

      img.src = base64Image;
    });
  }

  /**
   * Generate cache key for try-on result
   * @param {string} personImage - Person image
   * @param {string} garmentImage - Garment image
   * @param {Object} options - Options
   * @returns {string} Cache key
   */
  generateCacheKey(personImage, garmentImage, options) {
    // Create a simple hash of the inputs
    const input = personImage.slice(-100) + garmentImage.slice(-100) + JSON.stringify(options);
    return btoa(input).slice(0, 32);
  }

  /**
   * Cache try-on result
   * @param {string} key - Cache key
   * @param {Object} result - Result to cache
   */
  cacheResult(key, result) {
    // Implement LRU cache behavior
    if (this.cache.size >= this.maxCacheSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, {
      ...result,
      cachedAt: Date.now()
    });
  }

  /**
   * Poll for async VTO result
   * @param {string} jobId - Job ID to poll
   * @param {number} maxAttempts - Maximum polling attempts (default: 60)
   * @param {number} interval - Polling interval in ms (default: 2000)
   * @returns {Promise<Object>} Job result
   */
  async pollForResult(jobId, maxAttempts = 60, interval = 2000) {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const result = await this.makeApiCall(`/virtual-try-on/status/${jobId}`, 'GET');
        
        if (result.status === 'completed' || result.status === 'failed') {
          return result;
        }

        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, interval));
      } catch (error) {
        console.error(`Poll attempt ${attempt + 1} failed:`, error);
        // Continue polling even if one attempt fails
        await new Promise(resolve => setTimeout(resolve, interval));
      }
    }

    throw new Error('Polling timeout - virtual try-on took too long');
  }

  /**
   * Make API call to backend
   * @param {string} endpoint - API endpoint
   * @param {string} method - HTTP method
   * @param {Object} data - Request data
   * @returns {Promise<Object>} API response
   */
  async makeApiCall(endpoint, method = 'GET', data = null) {
    const token = this.getAuthToken();

    console.log('[VTOService] Making API call with auth:', {
      endpoint,
      method,
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'none'
    });

    if (!token) {
      throw new Error('Authentication required. Please log in to use virtual try-on.');
    }

    const config = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    };

    if (data && method !== 'GET') {
      config.body = JSON.stringify(data);
    }

    const response = await fetch(`${this.backendUrl}${endpoint}`, config);

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication failed. Please log in again to use virtual try-on.');
      }

      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Handle and format errors
   * @param {Error} error - Original error
   * @returns {Error} Formatted error
   */
  handleError(error) {
    if (error.message.includes('quota')) {
      return new Error('Virtual try-on quota exceeded. Please try again later.');
    } else if (error.message.includes('invalid image')) {
      return new Error('Image quality is too low. Please upload a clearer photo.');
    } else if (error.message.includes('person not detected')) {
      return new Error('Person not detected in image. Please upload a photo with a clear view of a person.');
    } else if (error.message.includes('413') || error.message.includes('too large')) {
      return new Error('Images are too large. Please upload smaller photos (max 10MB each).');
    } else if (error.message.includes('temporarily unavailable')) {
      return new Error('Virtual try-on service temporarily unavailable. Please try again later.');
    } else if (error.message.includes('network') || error.message.includes('fetch')) {
      return new Error('Network error. Please check your connection and try again.');
    }

    return error;
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache stats
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      keys: Array.from(this.cache.keys())
    };
  }

  /**
   * Save VTO result to backend using RTK Query
   * @param {Object} vtoData - VTO result data
   * @param {string} vtoData.personImageId - Person image ID from backend
   * @param {string} vtoData.closetClothesId - Clothing item ID from backend
   * @param {string} vtoData.resultImageUrl - Result image URL from VTO API
   * @param {Object} vtoData.metadata - Additional metadata
   * @returns {Promise<Object>} Saved VTO result
   */
  async saveVTOResultToBackend(vtoData) {
    if (!this.store) {
      console.warn('Redux store not available, cannot save VTO result to backend');
      return null;
    }

    try {
      // Import the mutation dynamically to avoid circular dependencies
      const { useSaveVTOResultWithUploadsMutation } = await import('../redux/apis/endpoints/vtoResultApi');

      // Convert result image URL to File object for upload
      const resultImageFile = await this.urlToFile(vtoData.resultImageUrl, 'vto-result.jpg');

      // Generate thumbnail if needed
      let thumbnailImageFile = null;
      if (vtoData.generateThumbnail !== false) {
        const thumbnailUrl = await this.generateThumbnail(vtoData.resultImageUrl);
        thumbnailImageFile = await this.urlToFile(thumbnailUrl, 'vto-thumbnail.jpg');
      }

      // Prepare save data
      const saveData = {
        personImageId: vtoData.personImageId,
        closetClothesId: vtoData.closetClothesId,
        resultImageFile,
        thumbnailImageFile,
        garmentType: vtoData.garmentType,
        garmentSource: vtoData.garmentSource || 'user_upload',
        processingTime: vtoData.processingTime,
        apiCost: vtoData.apiCost,
        generationMetadata: {
          ...vtoData.metadata,
          timestamp: Date.now(),
          version: '1.0'
        },
        confidence: vtoData.confidence,
        garmentFit: vtoData.garmentFit || 'good'
      };

      // Use RTK Query mutation through store dispatch
      const result = await this.store.dispatch(
        enhancedVTOResultApi.endpoints.saveVTOResultWithUploads.initiate(saveData)
      ).unwrap();

      console.log('[VTOService] VTO result saved to backend:', result);
      return result;

    } catch (error) {
      console.error('[VTOService] Failed to save VTO result to backend:', error);
      throw error;
    }
  }

  /**
   * Convert data URL to File object
   * @param {string} dataUrl - Data URL to convert
   * @param {string} filename - Filename for the file
   * @returns {Promise<File>} File object
   */
  async urlToFile(dataUrl, filename) {
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type });
  }

  /**
   * Generate thumbnail from image URL
   * @param {string} imageUrl - Source image URL
   * @param {number} maxSize - Maximum thumbnail size (default 200px)
   * @returns {Promise<string>} Thumbnail data URL
   */
  async generateThumbnail(imageUrl, maxSize = 200) {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Calculate thumbnail dimensions
        let { width, height } = img;
        const ratio = Math.min(maxSize / width, maxSize / height);
        width *= ratio;
        height *= ratio;

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        try {
          const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);
          resolve(thumbnailUrl);
        } catch (error) {
          reject(new Error('Failed to generate thumbnail'));
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image for thumbnail generation'));
      };

      img.src = imageUrl;
    });
  }

  /**
   * Enhanced try-on method that saves results to backend with multi-item support
   * @param {Object} params - Try-on parameters
   * @param {Object} params.baseImageSource - Base image source with type and id
   * @param {string} params.clothingItemId - New clothing item ID from backend
   * @param {string} params.garmentImageUrl - Garment image URL for VTO API
   * @param {Array} params.currentlyWornItemIds - Array of currently worn item IDs for caching
   * @param {Object} params.options - Try-on options
   * @param {boolean} params.saveToBackend - Whether to save result to backend (default: true)
   * @returns {Promise<Object>} Try-on result with backend save status
   */
  async tryOnGarmentWithBackendSave(params) {
    const {
      baseImageSource, // { type: 'vto_result' | 'person_image', id: number }
      clothingItemId,
      garmentImageUrl,
      currentlyWornItemIds = [], // Array of currently worn item IDs for caching
      options = {},
      saveToBackend = true
    } = params;

    try {
      // Build complete list of all clothing items (previous + new)
      const allClothingItemIds = [...currentlyWornItemIds, clothingItemId];

      // Create layered items metadata
      const layeredItems = [
        ...currentlyWornItemIds.map((itemId, index) => ({
          id: itemId,
          order: index + 1
        })),
        {
          id: clothingItemId,
          order: currentlyWornItemIds.length + 1,
          name: options.garmentName,
          category: options.garmentType
        }
      ];

      console.log('[VTOService] ID-Based Multi-item VTO request:', {
        baseImageSource,
        newItemId: clothingItemId,
        currentlyWornCount: currentlyWornItemIds.length,
        totalItems: allClothingItemIds.length,
        allItemIds: allClothingItemIds
      });

      // Prepare request data with baseImageSource
      const requestData = {
        baseImageSource, // Send ID instead of URL
        garmentImage: garmentImageUrl,
        garmentType: options.garmentType,
        options: {
          preserveBackground: options.preserveBackground !== false,
          fitPreference: options.fitPreference || 'fitted',
          currentlyWornItemIds, // For caching - items already worn
          garmentId: clothingItemId, // New item being added
          userId: options.userId,
          // layeredItems is for frontend metadata only, backend uses currentlyWornItemIds + garmentId
          layeredItems: [{ // Only send the NEW item in layeredItems for backend metadata
            id: clothingItemId,
            order: 1,
            name: options.garmentName
          }],
          ...options
        }
      };

      // CRITICAL: Log what we're sending to backend
      console.log('[VTOService] SENDING TO BACKEND:', {
        baseImageSource,
        hasGarmentImage: !!garmentImageUrl,
        garmentType: options.garmentType,
        currentlyWornItemIds,
        garmentId: clothingItemId
      });

      // Make async API call (returns immediately with jobId, or cached result directly)
      const startTime = Date.now();
      const response = await this.makeApiCall('/virtual-try-on/try-on-async', 'POST', requestData);

      // Handle cached response (returned directly without jobId)
      if (response.success && response.cached && response.result) {
        console.log('[VTOService] VTO cache hit - returning cached result directly');
        const vtoResult = response.result;
        const processingTime = Date.now() - startTime;

        // Extract originalPersonImageId from backend response or baseImageSource
        const originalPersonImageId = vtoResult.metadata?.originalPersonImageId || 
                                      (baseImageSource.type === 'person_image' ? baseImageSource.id : null);

        // Prepare result data for cached response
        return {
          ...vtoResult,
          processingTime,
          originalPersonImageId,
          clothingItemId,
          allClothingItemIds,
          layeredItems,
          isLayered: allClothingItemIds.length > 1,
          garmentType: options.garmentType,
          confidence: vtoResult.confidence,
          garmentFit: vtoResult.garmentFit || 'good',
          metadata: {
            ...(vtoResult.metadata || {}),
            garmentImage: options.garmentImage || vtoResult.metadata?.garmentImage,
            garmentName: options.garmentName || vtoResult.metadata?.garmentName,
            garmentType: options.garmentType || vtoResult.metadata?.garmentType
          }
        };
      }

      // Non-cached response - need jobId for polling
      if (!response.success || !response.jobId) {
        // A shared try-on without a subscription isn't a failure to retry — the
        // caller should open the subscription modal, so carry the code through.
        if (response.code === 'purchase_required' || response.code === 'subscription_required') {
          const err = new Error(response.message || 'Buy credits or subscribe to continue');
          err.code = response.code;
          throw err;
        }
        throw new Error(response.message || 'Failed to start virtual try-on');
      }

      // Poll for result
      const result = await this.pollForResult(response.jobId);

      if (result.status !== 'completed') {
        throw new Error(result.error || 'Virtual try-on failed');
      }

      const vtoResult = result.result;
      const processingTime = Date.now() - startTime;

      if (!vtoResult.success) {
        throw new Error(vtoResult.error || 'Virtual try-on failed');
      }

      console.log('[VTOService] VTO result:', {
        success: vtoResult.success,
        cached: vtoResult.cached,
        backendSaved: vtoResult.backendSaved,
        hasResultImage: !!vtoResult.imageUrl
      });

      // Extract originalPersonImageId from backend response or baseImageSource
      const originalPersonImageId = vtoResult.metadata?.originalPersonImageId || 
                                    vtoResult.backendResult?.generationMetadata?.originalPersonImageId ||
                                    (baseImageSource.type === 'person_image' ? baseImageSource.id : null);

      // Prepare result data
      const resultData = {
        ...vtoResult,
        processingTime,
        originalPersonImageId,
        clothingItemId,
        allClothingItemIds,
        layeredItems,
        isLayered: allClothingItemIds.length > 1,
        garmentType: options.garmentType,
        confidence: vtoResult.confidence,
        garmentFit: vtoResult.garmentFit || 'good',
        // Ensure metadata is populated with options if missing from backend response
        metadata: {
          ...(vtoResult.metadata || {}),
          garmentImage: options.garmentImage || vtoResult.metadata?.garmentImage,
          garmentName: options.garmentName || vtoResult.metadata?.garmentName,
          garmentType: options.garmentType || vtoResult.metadata?.garmentType
        }
      };

      return resultData;

    } catch (error) {
      console.error('Enhanced try-on error:', error);
      throw error;
    }
  }

  /**
   * Get VTO results from backend using RTK Query
   * @param {Object} filters - Query filters
   * @returns {Promise<Object>} VTO results from backend
   */
  async getVTOResultsFromBackend(filters = {}) {
    if (!this.store) {
      console.warn('Redux store not available, cannot fetch VTO results from backend');
      return { results: [], pagination: { total: 0, hasMore: false } };
    }

    try {
      // Use RTK Query to fetch results
      const result = await this.store.dispatch(
        enhancedVTOResultApi.endpoints.getUserVTOResults.initiate(filters)
      ).unwrap();

      return result;
    } catch (error) {
      console.error('Failed to fetch VTO results from backend:', error);
      return { results: [], pagination: { total: 0, hasMore: false } };
    }
  }

  /**
   * Check if VTO result exists in cache (fast, no generation)
   * @param {Object} params - Parameters
   * @param {Object} params.baseImageSource - Base image source { type, id }
   * @param {Array<number>} params.clothingItemIds - Array of clothing item IDs
   * @returns {Promise<Object>} Cache check result { cached, creditBalance, result? }
   */
  async checkCacheOnly({ baseImageSource, clothingItemIds }) {
    try {
      if (!baseImageSource || !clothingItemIds || !Array.isArray(clothingItemIds)) {
        throw new Error('baseImageSource and clothingItemIds array are required');
      }

      const sortedIds = [...clothingItemIds].sort((a, b) => a - b);

      const response = await this.makeApiCall('/virtual-try-on/check-cache', 'POST', {
        baseImageSource,
        clothingItemIds: sortedIds
      });

      return response;
    } catch (error) {
      console.error('Check cache error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get or generate VTO result for a specific clothing combination
   * Checks backend cache first, generates new result if not found
   * @param {Object} params - Parameters
   * @param {Object} params.baseImageSource - Base image source { type, id }
   * @param {Array<number>} params.clothingItemIds - Array of clothing item IDs
   * @param {boolean} params.useRemovedImage - Whether to use background-removed version
   * @param {Object} params.options - Additional options
   * @returns {Promise<Object>} VTO result (cached or newly generated)
   */
  async getOrGenerateVTOForCombination({
    baseImageSource,
    clothingItemIds,
    useRemovedImage = true,
    options = {}
  }) {
    try {
      // Validate inputs
      if (!baseImageSource || !clothingItemIds || !Array.isArray(clothingItemIds)) {
        throw new Error('baseImageSource and clothingItemIds array are required');
      }

      if (clothingItemIds.length === 0) {
        throw new Error('clothingItemIds array cannot be empty');
      }

      // Sort IDs for consistent cache key
      const sortedIds = [...clothingItemIds].sort((a, b) => a - b);

      console.log('[VTOService] Get or Generate VTO for combination:', {
        baseImageSource,
        clothingItemIds: sortedIds,
        useRemovedImage,
        operation: options.operation || 'get_or_generate'
      });

      // Build request payload
      const payload = {
        baseImageSource,
        clothingItemIds: sortedIds,
        useRemovedImage,
        operation: options.operation || 'get_or_generate',
        removedItemId: options.removedItemId
      };

      // Call backend endpoint
      const response = await this.makeApiCall(
        '/virtual-try-on/get-or-generate',
        'POST',
        payload
      );

      if (!response || !response.success) {
        throw new Error(response?.message || 'Failed to get or generate VTO result');
      }

      const responseImageUrl = Array.isArray(response.resultImage) ? response.resultImage[0]?.url || response.resultImage[0] : response.resultImage;
      const responseRemovedBgImageUrl = Array.isArray(response.removedBgImage) ? response.removedBgImage[0]?.url || response.removedBgImage[0] : response.removedBgImage;

      console.log('[VTOService] Get or Generate VTO response:', {
        success: response.success,
        cached: response.cached,
        hasResultImage: !!responseImageUrl,
        layeredItemsCount: response.layeredItems?.length || 0
      });

      // Return standardized result format
      return {
        success: true,
        imageUrl: responseImageUrl,
        removedBgImage: responseRemovedBgImageUrl,
        savedResultId: response.id,
        cached: response.cached || false,
        backendSaved: true,
        metadata: response.generationMetadata || {},
        allClothingItemIds: sortedIds,
        layeredItems: response.layeredItems || [],
        closetClothes: response.closetClothes || []
      };
    } catch (error) {
      console.error('Get or generate VTO error:', error);
      throw this.handleError(error);
    }
  }
}

// Export singleton instance
export default new VirtualTryOnService();